import { DateObject, Value } from "react-multi-date-picker";
import { AffiliatesApiData, convertedAffiliatesData } from "./interfaces";
import { formatPrice } from "../../../helpers/dataConverts";

export const formatDate = (date: Value): string => {
  if (!date) return "";

  const dateObj: Date =
    date instanceof Date
      ? date
      : date instanceof DateObject
      ? date.toDate()
      : new Date(date as string | number);

  const day: number = dateObj.getDate();
  const month: string = dateObj.toLocaleString("default", { month: "short" });
  const year: number = dateObj.getFullYear();

  return `${day} ${month} ${year}`;
};

export const getDateRangeString = (values: Value[]): string => {
  if (!values || values.length === 0) {
    return "Select date range";
  }

  if (Array.isArray(values) && values.length >= 2) {
    return `${formatDate(values[0])} - ${formatDate(values[1])}`;
  }

  return formatDate(values[0]);
};

export const formatToYYYYMMDD = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseFromYYYYMMDD = (dateString: string): Date => {
  const [year, month, day] = dateString.split("-");
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

export function transformAffiliatesApiDataToTableData(
  apiData: AffiliatesApiData[]
): convertedAffiliatesData[] {
  return apiData?.map((item: any) => ({
    id: item.id,
    documentId: item.documentId,
    leadName: item.invitedUser ? item.invitedUser.fullName : "-",
    leadStatus: item.leadStatus,
    registrationDate: item.registrationDate,
    endDate: item.endDate,
    earned: item.earned,
    pending: item.pending,
    owner: item.owner,
    invitedUser: item.invitedUser,
  }));
}

export const calculateTimeRemaining = (
  endDateStr: string,
  locale = "en"
): { text: string; isWarning: boolean } => {
  const endDate = new Date(endDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (endDate <= today) {
    return {
      text: locale === "he" ? "0 ימים" : "0 days",
      isWarning: true,
    };
  }

  const diffMs = endDate.getTime() - today.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  const isWarning = diffDays < 3;

  const months = Math.floor(diffDays / 30);
  const remainingDays = diffDays % 30;

  const monthText = {
    en: months === 1 ? "month" : "months",
    he: months === 1 ? "חודש" : "חודשים",
  }[locale];

  const dayText = {
    en: remainingDays === 1 ? "day" : "days",
    he: remainingDays === 1 ? "יום" : "ימים",
  }[locale];

  let text = "";
  if (months === 0) {
    text = `${remainingDays} ${dayText}`;
  } else if (remainingDays === 0) {
    text = `${months} ${monthText}`;
  } else {
    if (locale === "he") {
      text = `${months} ${monthText} ו-${remainingDays} ${dayText}`;
    } else {
      text = `${months} ${monthText} ${remainingDays} ${dayText}`;
    }
  }

  return { text, isWarning };
};

export function calculateTotalEarned(
  data: AffiliatesApiData[],
  formatResult = false
) {
  if (!Array.isArray(data)) {
    return formatResult ? "₪0" : 0;
  }

  const total = data.reduce((sum, item) => {
    if (!item.earned) {
      return sum;
    }

    const earnedValue = parseFloat(item.earned.replace("₪", "").trim()) || 0;

    return sum + earnedValue;
  }, 0);

  return formatResult ? `₪${formatPrice(total)}` : formatPrice(total);
}

export const formatDateDMY = (
  dateString: string,
  language: string = "en"
): string => {
  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date format");
    }

    const locale = language === "en" ? "en-US" : "he-IL";

    const day = date.getDate();

    const month = date.toLocaleString(locale, { month: "short" });

    const year = date.getFullYear();

    if (language === "en") {
      return `${day} ${month} ${year}`;
    } else {
      return `${day} ${month} ${year}`;
    }
  } catch (error) {
    console.error("Error formatting date:", error);
    return dateString;
  }
};

export const extractNumberFromCurrency = (currencyString: string): number => {
  if (!currencyString) return 0;

  const value = currencyString.replace(/₪/g, "").trim();

  const numericValue = parseFloat(value);

  return isNaN(numericValue) ? 0 : numericValue;
};

export function displayDate(dataRange: any, rtl?: boolean): string {
  const startDate = new Date(dataRange[0]).toLocaleDateString(
    rtl ? "he-IL" : "en-US",
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  );
  const endDate = new Date(dataRange[1]).toLocaleDateString(
    rtl ? "he-IL" : "en-US",
    {
      year: "numeric",
      month: "short",
      day: "numeric",
    }
  );
  return `${startDate} - ${endDate}`;
}
